import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { GoalDetail } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import { usePermission } from '@/hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { MyGoalsTab } from './components/MyGoalsTab';
import { TeamGoalsTab } from './components/TeamGoalsTab';
import { GoalConfigTab } from './components/GoalConfigTab';
import { CreateGoalModal } from './components/CreateGoalModal';
import { EditGoalModal } from './components/EditGoalModal';
import { LogProgressModal } from './components/LogProgressModal';
import { ReviewGoalModal } from './components/ReviewGoalModal';
import { GoalDetailDrawer } from './components/GoalDetailDrawer';

type TabKey = 'my' | 'team' | 'review' | 'config';

/**
 * Single Goals surface with permission-gated tabs (UI Pattern B). An employee sees
 * only "My Goals"; managers additionally get the team table, the review queue and
 * the module's own category config.
 */
export function GoalsPage() {
  const perms = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();

  const [createOpen, setCreateOpen] = useState(false);
  const [editGoal, setEditGoal] = useState<GoalDetail | null>(null);
  const [progressGoal, setProgressGoal] = useState<GoalDetail | null>(null);
  const [reviewGoal, setReviewGoal] = useState<GoalDetail | null>(null);

  const availableTabs = useMemo(() => {
    const tabs: { key: TabKey; label: string }[] = [];
    if (perms.has(PERMISSIONS.GOALS_VIEW_OWN)) tabs.push({ key: 'my', label: 'My Goals' });
    if (perms.has(PERMISSIONS.GOALS_VIEW_TEAM) || perms.has(PERMISSIONS.GOALS_VIEW_ALL)) {
      tabs.push({ key: 'team', label: 'Team Goals' });
    }
    if (perms.has(PERMISSIONS.GOALS_REVIEW)) tabs.push({ key: 'review', label: 'Reviews' });
    if (perms.has(PERMISSIONS.GOALS_CONFIG)) tabs.push({ key: 'config', label: 'Config' });
    return tabs;
  }, [perms]);

  const requestedTab = searchParams.get('tab') as TabKey | null;
  const activeTab: TabKey = availableTabs.some((t) => t.key === requestedTab)
    ? requestedTab!
    : (availableTabs[0]?.key ?? 'my');

  // The drawer is shared by every tab and deep-linkable via ?open=<goalId>, which is
  // what the goal notifications link to.
  const openId = searchParams.get('open');

  function setTab(key: string) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    next.delete('open');
    // `sub` only means anything inside Config; leaving it behind puts a dead param on
    // every other tab's URL.
    if (key !== 'config') next.delete('sub');
    setSearchParams(next, { replace: true });
  }

  function openGoal(id: string) {
    const next = new URLSearchParams(searchParams);
    next.set('tab', activeTab);
    next.set('open', id);
    setSearchParams(next, { replace: true });
  }

  function closeGoal() {
    const next = new URLSearchParams(searchParams);
    next.delete('open');
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Goals</h1>
          <p className="text-sm text-muted-foreground">
            Set measurable targets with deadlines, log progress, and review outcomes
          </p>
        </div>
        <PermissionGate permission={PERMISSIONS.GOALS_CREATE}>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New Goal
          </Button>
        </PermissionGate>
      </div>

      {availableTabs.length > 1 && (
        <Tabs tabs={availableTabs} active={activeTab} onChange={setTab} />
      )}

      <div className="mt-1">
        {activeTab === 'my' && <MyGoalsTab onOpen={openGoal} />}
        {activeTab === 'team' && <TeamGoalsTab onOpen={openGoal} />}
        {activeTab === 'review' && (
          <TeamGoalsTab
            onOpen={openGoal}
            lockedStatus="submitted"
            showStats={false}
            emptyMessage="Nothing waiting on your review right now"
          />
        )}
        {activeTab === 'config' && <GoalConfigTab />}
      </div>

      <CreateGoalModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <GoalDetailDrawer
        goalId={openId}
        onClose={closeGoal}
        onEdit={setEditGoal}
        onLogProgress={setProgressGoal}
        onReview={setReviewGoal}
      />

      <EditGoalModal goal={editGoal} onClose={() => setEditGoal(null)} />
      <LogProgressModal goal={progressGoal} onClose={() => setProgressGoal(null)} />
      <ReviewGoalModal goal={reviewGoal} onClose={() => setReviewGoal(null)} />
    </div>
  );
}
